import { useMemo, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentTimeFrame, setCurrentIndicators } from '../../redux/stockData/nasdaq/stockDataReducer';
import { fetchStockDataThunk } from '../../redux/stockData/nasdaq/stockDataActions';

import ChartContent from './ChartContent';
import { darkTheme, lightTheme } from '../theme';
import { useTheme as useSiteTheme } from '../../theme/ThemeContext';
import { unwrapChartResponse } from '../../utils/unwrapChartResponse';

const TIMEFRAME_MAP = { '1D': 'daily', '1W': 'weekly', '1M': 'monthly', '1Y': 'yearly' };

const INDICATOR_MAP = {
  SMA: 'sma',
  EMA: 'ema',
  MACD: 'macd',
  'Bollinger-Bands': 'bollinger_bands',
  ATR: 'atr',
  RSI: 'rsi',
  'Stochastic-Oscillator': 'stochastic_oscillator',
  ADX: 'adx',
  OBV: 'obv',
  CMF: 'cmf',
  CCI: 'cci',
  'Williams-R': 'williams_r',
  AROON: 'aroon',
  MFI: 'mfi',
  'Parabolic-SAR': 'parabolic_sar',
  Ichimoku: 'ichimoku',
  ROC: 'roc',
  CMO: 'cmo',
  'Keltner-Channels': 'keltner_channels',
  RVI: 'rvi',
  'Elder-Ray': 'elder_ray',
};
const INDICATOR_OPTIONS = Object.keys(INDICATOR_MAP);

const ChartContainer = () => {
  const { theme: siteThemeMode } = useSiteTheme();
  const theme = siteThemeMode === 'light' ? lightTheme : darkTheme;
  const dispatch = useDispatch();
  const { data, loading, error, currentSymbol, currentTimeFrame, currentIndicators } = useSelector((s) => s.stockData);

  const responseArray = useMemo(() => unwrapChartResponse(data), [data]);
  const hasValidData = useMemo(() => Array.isArray(responseArray) && responseArray.length > 0, [responseArray]);

  const handleTimeFrameChange = useCallback((tf) => dispatch(setCurrentTimeFrame(tf)), [dispatch]);
  const handleIndicatorsChange = useCallback((next) => dispatch(setCurrentIndicators(next)), [dispatch]);

  useEffect(() => {
    if (currentSymbol && currentTimeFrame) {
      const mapped = currentIndicators.map((i) => INDICATOR_MAP[i]).filter(Boolean);
      dispatch(fetchStockDataThunk({ symbol: currentSymbol, timeFrame: TIMEFRAME_MAP[currentTimeFrame], indicators: mapped }));
    }
  }, [dispatch, currentSymbol, currentTimeFrame, currentIndicators]);

  const containerClass = 'flex flex-col rounded-2xl overflow-hidden border';
  const containerStyle = { background: theme.background.panel, borderColor: theme.border };

  // Only the *first* load (no data on screen yet) replaces the whole card
  // with a spinner. A timeframe/indicator change re-triggers `loading`
  // too, but tearing down the entire toolbar into a spinner every time --
  // closing an open indicator picker, resetting scroll -- was needlessly
  // jarring for what's just a background refetch; ChartContent keeps
  // rendering the still-valid previous data underneath instead.
  if (loading && !hasValidData) {
    return (
      <div className={containerClass} style={containerStyle}>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-3">
            <div
              className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `2px solid ${theme.border}`, borderTopColor: theme.brand.indigo }}
            />
            <p className="text-sm text-gray-500 dark:text-white/40">Loading chart data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={containerClass} style={containerStyle}>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-3 text-center px-4">
            <div className="text-rose-600 dark:text-rose-400 text-2xl">⚠️</div>
            {/* state.stockData.error is now always a plain string (see
                stockDataReducer.js's extractErrorMessage fix) -- this used
                to read error.detail/error.message off that same string,
                which are always undefined, so a real failure always fell
                through to the hardcoded "Unknown error" fallback below. */}
            <p className="text-gray-600 dark:text-white/60">Error loading data: {error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-1 px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-br from-violet-600 to-indigo-600 text-white hover:opacity-90 transition-opacity"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!hasValidData) {
    return (
      <div className={containerClass} style={containerStyle}>
        <div className="flex items-center justify-center h-96">
          <div className="flex flex-col items-center gap-2 text-center">
            <div className="text-gray-300 dark:text-white/20 text-2xl">📊</div>
            <p className="text-gray-500 dark:text-white/50">No data available</p>
            <p className="text-sm text-gray-400 dark:text-white/30">Try selecting a different symbol or time frame</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <ChartContent
      containerClass={containerClass}
      containerStyle={containerStyle}
      responseArray={responseArray}
      indicatorsData={data?.indicators || {}}
      currentSymbol={currentSymbol}
      currentTimeFrame={currentTimeFrame}
      currentIndicators={currentIndicators}
      indicatorOptions={INDICATOR_OPTIONS}
      onTimeFrameChange={handleTimeFrameChange}
      onIndicatorsChange={handleIndicatorsChange}
      isRefreshing={loading}
    />
  );
};

export default ChartContainer;
